import React from 'react'
import { TrackingInterface, TrackingEvents, formatDate } from '@christies/react/utils'
import { FeatureHeader } from '@christies/react/components/Headers'
import { Image, Video } from '@christies/react/components/Editorial'
import { getAspectRatio, hasSpecificFieldValues, videoSourceFormatter } from '../../utils'
import { SocialMediaIcons, VideoType } from '../../sharedTypes'
import { ImageValue, ParsedAnchorValue, SelectValue, SitecoreGlobal, withParsedContext } from '../../withJSSContext'
import { Theme } from '@christies/react/foundations'
import { TFunction } from 'i18next'
import { isClient } from '../../../../helpers'
import { LinkCta } from '@christies/react/dist/lib/components/Headers/FeatureHeader/FeatureHeader.types'
import { XPosition } from '@christies/react/typings'
import {
  Email,
  Facebook,
  Instagram,
  Pinterest,
  Twitter,
  WeChat,
  Weibo,
  YouTube,
  Youku,
  X,
} from '@christies/react/icons'

const socialMediaIcons: Record<SocialMediaIcons, React.FC> = {
  email: Email,
  facebook: Facebook,
  instagram: Instagram,
  pinterest: Pinterest,
  twitter: Twitter,
  wechat: WeChat,
  weibo: Weibo,
  youku: Youku,
  youtube: YouTube,
  x: X,
}

interface Props {
  pageEditing: boolean
  parsedFields: {
    'Story Media Endpoint': string
    'Story Types Endpoint': string
    'Story Categories Endpoint': string
  }
  parsedRouteFields: {
    Title: string
    'Secondary Title': string
    'Secondary Title Alignment': SelectValue<XPosition>
    'Video Image': ImageValue
    'Video Source': SelectValue<VideoType>
    'Video Description': string
    'Hide Video': boolean
    'Story Theme': SelectValue<Theme | undefined>
    'Base Image': ImageValue
    'Image Description': string
    'Image Type': SelectValue<'8' | '12'>
    'Image Caption': string
    'Image Link': ParsedAnchorValue
    'Brightcove Id'?: string
    'Youtube Id'?: string
    'Embedded Video'?: string
    'Story Type': SelectValue[]
    'Story Category': SelectValue[]
    'Story Media': SelectValue[]
    'Show on Lightbox': boolean
    'Story Date': string
    'Title Font Style'?: SelectValue<'sans-serif' | 'serif'>
    'Secondary Title Font Style'?: SelectValue<'sans-serif' | 'serif'>
    'Author by Text': string
    'Author Label': string
    'Screen Reader Caption': string
  }
  parsedSitecoreContext: {
    'Brightcove Account Id'?: string
    'Brightcove Video Url'?: string
    'Youtube Video Url'?: string
    'Discovery.Feature.FeIntegration.IntegrationData'?: SitecoreGlobal
    language: string
  }
  t: TFunction
}

// TODO only pass needed fields
const MediaComponent: React.FC<Props> = ({ parsedRouteFields, parsedSitecoreContext, t }) => {
  const videoFallbackImage = parsedRouteFields?.['Video Image']?.src
  const videoSource = parsedRouteFields['Video Source']?.key
  const baseImage = getAspectRatio({
    ...parsedRouteFields['Base Image'],
    alt: parsedRouteFields['Screen Reader Caption'] || parsedRouteFields['Base Image']?.alt,
  })
  const theme = parsedRouteFields?.['Story Theme']?.key
  const hideVideo = parsedRouteFields['Hide Video']

  const videoUrl = videoSourceFormatter({
    videoType: videoSource,
    brightcove: {
      videoUrl: parsedSitecoreContext?.['Brightcove Video Url'],
      accountId: parsedSitecoreContext?.['Brightcove Account Id'],
      videoId: parsedRouteFields?.['Brightcove Id'],
    },
    youtube: {
      videoUrl: parsedSitecoreContext?.['Youtube Video Url'],
      videoId: parsedRouteFields?.['Youtube Id'],
    },
    embedded: {
      videoUrl: parsedRouteFields?.['Embedded Video'],
    },
  })

  const isVideo = (!hideVideo && videoSource) || (hideVideo && !baseImage && videoFallbackImage)

  return videoSource || baseImage || videoFallbackImage ? (
    isVideo ? (
      <Video
        description={parsedRouteFields['Video Description']}
        lgWidth={12}
        imageUrl={videoFallbackImage}
        videoUrl={hideVideo ? undefined : videoUrl}
        videoType={videoSource}
        theme={theme}
        $position="bottom"
      />
    ) : (
      <Image
        image={baseImage}
        theme={theme}
        description={parsedRouteFields?.['Image Description']}
        lgWidth={parsedRouteFields['Image Type']?.key}
        cta={{
          text: parsedRouteFields?.['Image Caption'],
          href: parsedRouteFields?.['Image Link']?.href,
          target: parsedRouteFields?.['Image Link']?.target,
        }}
        t={{
          galleryAccessibilityLabel: t('foundation-discovery-accessibility-images-labels-open-image-in-gallery'),
          linkAccessibilityLabel: t('foundation-discovery-accessibility-images-labels-open-link'),
        }}
        $position="bottom"
      />
    )
  ) : null
}

const MagazinesArticleHeaderModule: React.FC<Props> = (props) => {
  const { parsedFields, parsedRouteFields, parsedSitecoreContext, t } = props
  const storyShareList = parsedSitecoreContext['Discovery.Feature.FeIntegration.IntegrationData']?.share.items || []
  const clientId = parsedSitecoreContext['Discovery.Feature.FeIntegration.IntegrationData']?.share.client_id || ''

  const storyTypesTags = parsedRouteFields?.['Story Type'] || []
  const storyCategoryTags = parsedRouteFields?.['Story Category'] || []
  const storyMediaTags = parsedRouteFields?.['Story Media'] || []

  const storyTypesEndpoint = parsedFields['Story Types Endpoint']
  const storyCategoriesEndpoint = parsedFields['Story Categories Endpoint']
  const storyMediaEndpoint = parsedFields['Story Media Endpoint']

  const getTagAnalytics = (type: string): TrackingInterface => {
    return {
      event: TrackingEvents.INTERACTION,
      data: {
        interaction: {
          type: `${type.toLowerCase()}`,
          location: 'stories:tags',
          destination: 'stories:all features',
        },
      },
    }
  }
  const getShareAnalytics = (platform: string): TrackingInterface => {
    return {
      event: TrackingEvents.SHARE,
      data: {
        share: {
          platform: `${platform.toLowerCase()}`,
        },
      },
    }
  }

  const getEmailLink = (): string => {
    const subject = encodeURIComponent(document.title)
    const message = encodeURIComponent(window.location.href)

    return `mailto:?subject=${subject}&body=${message}`
  }

  const getUrl = (type: string): string => {
    if (!isClient) return ''
    if (type === 'email') return getEmailLink()

    const url = window.location.href

    return `http://api.addthis.com/oexchange/0.8/forward/${type}/offer?url=${url}&pubid=${clientId}&ct=1`
  }

  const parseTagsData = (storyTagsType: SelectValue[], endpoint: string): LinkCta[] => {
    return storyTagsType?.map(({ value, key, id }) => ({
      text: value,
      href: endpoint?.replace('{tagId}', `${id?.replace(/-/g, '')}`),
      analytics: getTagAnalytics(key as string),
    }))
  }

  const combinedTagsList: LinkCta[] = [
    ...parseTagsData(storyTypesTags, storyTypesEndpoint),
    ...parseTagsData(storyCategoryTags, storyCategoriesEndpoint),
    ...parseTagsData(storyMediaTags, storyMediaEndpoint),
  ]

  const ctas = storyShareList.map(({ type, label_txt, aria_share_title_txt }) => ({
    icon: socialMediaIcons[type],
    text: label_txt,
    href: getUrl(type),
    ariaLabel: aria_share_title_txt,
    analytics: getShareAnalytics(type),
  }))

  const authorBy = parsedRouteFields?.['Author by Text']
  const authorLabel = parsedRouteFields?.['Author Label']
  const author = authorLabel && authorBy.concat(authorBy ? ' ' : '', authorLabel)

  return props.pageEditing || hasSpecificFieldValues(parsedRouteFields, ['Title', 'Secondary Title']) ? (
    <>
      <FeatureHeader
        author={author}
        title={parsedRouteFields?.['Title']}
        secondaryTitle={parsedRouteFields?.['Secondary Title']}
        secondaryTitleAlignment={parsedRouteFields?.['Secondary Title Alignment']?.key}
        date={formatDate(parsedRouteFields?.['Story Date'], null, parsedSitecoreContext.language, true)}
        titleFontVariant={parsedRouteFields?.['Title Font Style']?.key}
        secondaryTitleFontVariant={parsedRouteFields?.['Secondary Title Font Style']?.key}
        theme={parsedRouteFields?.['Story Theme']?.key}
        tags={combinedTagsList}
        shareModalProps={{
          modalRootId: 'modal-root',
          buttonText: t('feature-discovery-magazines-storyheader-sharebuttontext'),
          modalTitle: t('foundation-discovery-socialmedia-sharingdialog-dialogtitletext'),
          ctas: ctas,
          buttonAriaLabel: t('feature-discovery-magazines-storyheader-ariasharebuttontext'),
          closeButtonAriaLabel: t('foundation-discovery-socialmedia-sharingdialog-ariaclosebuttontext'),
          ariaLabel: t('foundation-discovery-socialmedia-sharingdialog-dialogtitletext'),
        }}
      />
      <MediaComponent {...props} />
    </>
  ) : null
}

export default withParsedContext(MagazinesArticleHeaderModule as React.FC)
